import { useCallback, useEffect, useMemo, useState } from 'react';
import apiPublic from '../../services/api-public';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import SpinnerLoader from '../layout/spinner-loader';

const stripeAppearance = {
  theme: 'stripe',

  variables: {
    colorPrimary: '#000',
    colorBackground: '#ffffff',
    colorText: '#000',
    fontFamily: 'Noto Sans, sans-serif',
  }
};

const StripePaymentElement = ({ onConfirmReady, onElementReady }) => {
    const stripe = useStripe();
    const elements = useElements();

    useEffect(() => {
        if (!stripe || !elements) {
            onConfirmReady(null);
            return;
        }

        onConfirmReady(async () => {
            if (!stripe || !elements) {
                return { error: { message: 'Payment form is still loading. Please try again.' } };
            }

            const result = await stripe.confirmPayment({
                elements,
                redirect: 'if_required'
            });

            if (result.error) {
                return { error: result.error };
            }

            if (result.paymentIntent?.status !== 'succeeded') {
                return { error: { message: 'Payment has not completed yet. Please try again.' } };
            }

            return { paymentIntent: result.paymentIntent };
        });

        return () => onConfirmReady(null);
    }, [elements, onConfirmReady, stripe]);

    return <>
        <PaymentElement onReady={() => onElementReady?.(true)} />
    </>;
};

const defaultTransactionError = 'There was an error processing your card. Please try again.';

const CreditCardPayment = ({fee, description, invoiceNumber, email, onClose, setTransactionResponse, buttonText, className, disabled }) => {
    const stripePromise = useMemo(() => loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY), []);
    const [paymentClientSecret, setPaymentClientSecret] = useState(null);
    const [confirmStripePayment, setConfirmStripePayment] = useState(null);
    const [loadingPaymentIntent, setLoadingPaymentIntent] = useState(false);
    const [submittingPayment, setSubmittingPayment] = useState(false);
    const [activeIntentKey, setActiveIntentKey] = useState(null);
    const [paymentElementReady, setPaymentElementReady] = useState(false);

    const paymentAmount = Number(fee);
    const isValidPaymentAmount = Number.isFinite(paymentAmount) && paymentAmount > 0;
    const intentKey = `${paymentAmount}|${description || ''}|${invoiceNumber || ''}|${email || ''}`;

    useEffect(() => {
        let isMounted = true;

        if (activeIntentKey && activeIntentKey !== intentKey) {
            setPaymentClientSecret(null);
            setConfirmStripePayment(null);
            setActiveIntentKey(null);
            setPaymentElementReady(false);
            setSubmittingPayment(false);
        }

        if (!isValidPaymentAmount) {
            setLoadingPaymentIntent(false);
            setPaymentClientSecret(null);
            setConfirmStripePayment(null);
            setActiveIntentKey(null);
            setPaymentElementReady(false);
            setSubmittingPayment(false);
            return;
        }

        const createPaymentIntent = async () => {
            if (disabled) {
                return;
            }

            if (paymentClientSecret && activeIntentKey === intentKey) {
                return;
            }

            setLoadingPaymentIntent(true);
            setPaymentClientSecret(null);
            setPaymentElementReady(false);
            setSubmittingPayment(false);

            try {
                const response = await apiPublic.post('/create-payment-intent', {
                    amount: paymentAmount,
                    email: email || '',
                    description: description || '',
                    invoiceNumber: invoiceNumber ? invoiceNumber.toString() : ''
                }, { noErrorHandling: true });

                if (!isMounted) {
                    return;
                }

                if (!response?.data?.success || !response?.data?.clientSecret) {
                    return;
                }

                setPaymentClientSecret(response.data.clientSecret);
                setActiveIntentKey(intentKey);
            }  finally {
                if (isMounted) {
                    setLoadingPaymentIntent(false);
                }
            }
        };

        createPaymentIntent();

        return () => {
            isMounted = false;
        };
    }, [activeIntentKey, description, disabled, email, intentKey, invoiceNumber, isValidPaymentAmount, paymentAmount, paymentClientSecret]);

    const onConfirmReady = useCallback((confirmFn) => {
        setConfirmStripePayment(() => confirmFn || null);
    }, []);

    const confirmPayment = async () => {
        if (disabled || submittingPayment) {
            return;
        }

        if (!confirmStripePayment) {
            const errorMessage = 'Payment form is still loading. Please try again.';
            setTransactionResponse?.({ paymentIntentId: "0", errors: [errorMessage] });
            return;
        }

        setSubmittingPayment(true);

        try {
            const result = await confirmStripePayment();
            if (result.error) {
                const errorMessage = result.error.message || defaultTransactionError;
                setTransactionResponse?.({ paymentIntentId: "0", errors: [errorMessage] });
                setSubmittingPayment(false);
                return;
            }
            onClose?.();
            setTransactionResponse?.({ paymentIntentId: result.paymentIntent.id, errors: [] });
            setSubmittingPayment(false);
        } catch {
            setTransactionResponse?.({ paymentIntentId: "0", errors: [defaultTransactionError] });
            setSubmittingPayment(false);
        }
    };

    return <div className={className || ''}>
        {loadingPaymentIntent && <SpinnerLoader />}
        {paymentClientSecret &&
            <Elements stripe={stripePromise} options={{ clientSecret: paymentClientSecret, appearance: stripeAppearance }}>
                <div className="stripe-payment-container">
                    <StripePaymentElement onConfirmReady={onConfirmReady} onElementReady={setPaymentElementReady} />
                    {paymentElementReady && <button type="button" className="btn btn-primary mt-3" onClick={confirmPayment} disabled={disabled || submittingPayment}>
                        {submittingPayment ? 'Processing Payment...' : (buttonText || 'Submit and Pay')}
                    </button>}
                </div>
            </Elements>}
    </div>;
}

export default CreditCardPayment;